/*
 * "Add set" — adds every purchasable member of the set in one request.
 *
 * Deliberately reuses Dawn's cart pipeline rather than reimplementing it:
 *   - posts to routes.cart_add_url with an `items` array (Cart Ajax API)
 *   - asks for the same sections Dawn's own cart element wants, then hands the
 *     response to its renderContents(), so the notification/drawer slides in
 *     and the header bag count updates exactly as for a normal Add to cart
 *   - publishes PUB_SUB_EVENTS.cartUpdate so existing subscribers react
 */
if (!customElements.get('complete-the-set')) {
  customElements.define(
    'complete-the-set',
    class CompleteTheSet extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-cts-add]');
        this.errorEl = this.querySelector('[data-cts-error]');
        if (!this.button) return;

        this.button.addEventListener('click', this.onClick.bind(this));
      }

      get cart() {
        // Same lookup order product-form.js uses.
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      setError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }

      setLoading(isLoading) {
        this.button.classList.toggle('is-loading', isLoading);
        this.button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      /*
       * Dawn's CartNotification.renderContents() reads `parsedState.key` to pick
       * which line to show. A single-variant /cart/add.js returns that key at the
       * top level, but an `items` add returns { items: [...] } with no key — the
       * selector then resolves to null and renderContents throws. Borrow the key
       * from the first added line so the notification has something to render.
       */
      normaliseForNotification(data) {
        if (data && !data.key && Array.isArray(data.items) && data.items.length) {
          return { ...data, key: data.items[0].key };
        }
        return data;
      }

      async onClick() {
        if (this.button.getAttribute('aria-disabled') === 'true') return;

        const ids = (this.button.dataset.variantIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
        if (!ids.length) return;

        this.setError('');
        this.setLoading(true);

        const cart = this.cart;
        const body = { items: ids.map((id) => ({ id: Number(id), quantity: 1 })) };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        let data;
        try {
          const response = await fetch(routes.cart_add_url, {
            ...fetchConfig('javascript'),
            body: JSON.stringify(body),
          });
          data = await response.json();
          // The Ajax API sets `status` only on failure.
          if (data.status) throw new Error(data.description || data.message || window.cartStrings.error);
        } catch (error) {
          console.error(error);
          this.setError(error.message || window.cartStrings.error);
          this.setLoading(false);
          return;
        }

        /*
         * Past this point the items ARE in the cart, so nothing here may surface
         * an error to the shopper — a rendering problem must not look like a
         * failed add. Worst case the bag count is briefly stale.
         */
        try {
          if (!cart) {
            window.location = routes.cart_url;
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'complete-the-set', cartData: data });
          cart.renderContents(this.normaliseForNotification(data));
        } catch (error) {
          // Already in the cart by now, so the bag must not be left stale.
          console.error('[complete-the-set] added to cart, but the cart UI failed to update', error);
          document.querySelector('cart-drawer')?.refreshFromServer?.();
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
